import { authenticatedMiddleware } from '../../permissions/middlewares/authenticatedMiddleware.js';
import { checkPermissionMiddleware } from '../../permissions/middlewares/checkPermissionMiddleware.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { ID } from '@adornis/baseql/baseqlTypes.js';
import { registerMutation, registerQuery, registerSubscription } from '@adornis/baseql/metadata/register.js';
import { publishFilter } from '@adornis/baseql/server/context.js';
import { watchQuery } from '@adornis/baseql/server/watchQuery.js';
import { selectionSet, type BaseQLSelectionSet } from '@adornis/baseql/utils/queryGeneration.js';
import { GLOBAL_CONTEXT } from '@adornis/users/db/a-roles.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { AdornisRoleToContextsWrapper } from '@adornis/users/db/roleToContextsWrapper.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { from, switchMap } from 'rxjs';
import { Abwesenheit } from '../../db/abwesenheit.js';
import type { PartOfDayEnum } from '../../db/enums/abwesenheiten.js';
import { Permission } from '../../permissions/roles.js';
import { generateAbwesenheitenCSV } from '../../server/csv-helpers.ts/abwesenheit-csv-helpers.js';
import { Person } from '../../users/entities/person.js';
import { deleteAbwesenheit, sendAbwesenheitCreationMail, sendLangzeitKrankheitMail } from '../server/absenceMails.js';
import { findDuplicateAbwesenheit } from '../server/findDuplicateAbwesenheit.js';

const sendCreationMail = registerMutation({
  operationName: 'sendAbwesenheitCreationMail',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [
    { type: () => String, name: 'abwesenheitID' },
    { type: () => Boolean, name: 'deleteMail' },
  ],
  resolve:
    (abwesenheitID: string, deleteMail = false) =>
    () =>
      sendAbwesenheitCreationMail(abwesenheitID, deleteMail),
});

const sendLangzeitMail = registerMutation({
  operationName: 'sendLangzeitKrankheitMail',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [{ type: () => ID, name: 'abwesenheitID' }],
  resolve: (abwesenheitID: string) => () => sendLangzeitKrankheitMail(abwesenheitID),
});

const removeAbwesenheit = registerMutation({
  operationName: 'deleteAbwesenheit',
  middlewares: [authenticatedMiddleware],
  type: () => Boolean,
  params: [{ type: () => ID, name: 'abwesenheitID' }],
  resolve: (abwesenheitID: string) => () => deleteAbwesenheit(abwesenheitID),
});

/** @returns the URL the generated CSV can be downloaded from */
const downloadAbwesenheitCSV = registerQuery({
  operationName: 'downloadAbwesenheitCSV',
  middlewares: [checkPermissionMiddleware(Permission.ABWESENHEIT_EXPORT, GLOBAL_CONTEXT)],
  type: () => String,
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve: () => async () => {
    const currentUser = await getMyself<Person>()({ roles: selectionSet(() => AdornisRoleToContextsWrapper, 2) });
    if (!currentUser || !currentUser.hasPermission(Permission.ABWESENHEIT_EXPORT, GLOBAL_CONTEXT)) {
      throw new ExpectedError('Sie haben nicht die nötige Berechtigung hierfür');
    }

    return generateAbwesenheitenCSV();
  },
});

const subscribeAllAbwesenheitenForPerson = registerSubscription({
  operationName: 'subscribeAllAbwesenheitenForPerson',
  middlewares: [authenticatedMiddleware],
  type: () => [Abwesenheit],
  params: [{ type: () => ID, name: 'personID' }],
  resolve: (personID: string) => (gqlFields: BaseQLSelectionSet<Abwesenheit>) =>
    from(publishFilter(Abwesenheit._class)).pipe(
      switchMap(pF => {
        const selector = { personID };
        return watchQuery(Abwesenheit._collectionName, pF ? { $and: [pF, selector] } : selector);
      }),
    ),
});

const getAbwesenheitByPersonIdAndStartEnde = registerQuery({
  operationName: 'getAbwesenheitByPersonIdAndStartEnde',
  middlewares: [authenticatedMiddleware],
  type: () => Abwesenheit,
  params: [
    { type: () => String, name: 'personID' },
    { type: () => Date, name: 'start' },
    { type: () => Date, name: 'end' },
    // ganztägige Abwesenheiten haben keinen Tagesabschnitt, der Duplikatscheck muss darauf auch matchen
    { type: () => String, name: 'partOfTheDay' },
    { type: () => String, name: '_class' },
  ],
  options: { requestHandlerMeta: { queryCache: { cachePolicy: 'bypass' } } },
  resolve: (personID: string, start: Date, end: Date, partOfTheDay: Maybe<PartOfDayEnum>, _class: string) => () =>
    findDuplicateAbwesenheit(personID, start, end, partOfTheDay, _class),
});

export const AbwesenheitAPI = {
  sendCreationMail,
  sendLangzeitMail,
  remove: removeAbwesenheit,
  downloadCSV: downloadAbwesenheitCSV,
  subscribeForPerson: subscribeAllAbwesenheitenForPerson,
  findDuplicate: getAbwesenheitByPersonIdAndStartEnde,
};
